import { useEffect, useMemo, useReducer, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { CalendarLeave, LeaveTypeId } from '@lms/shared-types'
import { ALL_LEAVE_TYPES, HIDDEN_LEAVE_TYPE } from '../lib/leave-type-palette'
import {
  TeamCalendarGrid,
  type PersonOut,
} from '../components/employee/TeamCalendarGrid'
import { CalendarToolbar } from '../components/primitives/CalendarToolbar'
import type { CalendarFilters } from '../components/primitives/CalendarToolbar'
import { CalendarDayPopover } from '../components/primitives/CalendarDayPopover'
import { CalendarLegend } from '../components/primitives/CalendarLegend'
import { getCalendar, listCalendarTeams } from '../api/calendar'
import { useSession } from '../auth/auth-context'
import { useEffectiveEmployeeId } from '../components/shell/effective-employee'
import { buildDayMetaWithSignals, leaveConflictsBlackout } from '../lib/calendar-derive'
import {
  teamColorForIndex,
  teamColorStyle,
  type TeamColorStyle,
} from '../lib/team-color-palette'
import { fmtMonthYear } from '../lib/format'
import type { MonthGridDayMeta } from '../components/primitives/MonthGrid'

const HR_PLUS_ROLES = new Set(['hr', 'director', 'exec_director'])

interface MonthCell {
  date: string | null
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function todayISO(): string {
  const d = new Date()
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
}

function defaultMonth(): string {
  const d = new Date()
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`
}

/** 7-column Sunday-first month grid; nulls for leading + trailing blanks. */
function buildMonthCells(month: string): MonthCell[] {
  const [yStr, mStr] = month.split('-')
  const y = Number(yStr)
  const m = Number(mStr)
  const firstDay = new Date(Date.UTC(y, m - 1, 1))
  const lastDay = new Date(Date.UTC(y, m, 0))
  // getUTCDay: 0=Sun..6=Sat — Sunday-first, so the offset is the raw index.
  const firstWeekdayIdx = firstDay.getUTCDay()
  const cells: MonthCell[] = []
  for (let i = 0; i < firstWeekdayIdx; i++) cells.push({ date: null })
  for (let day = 1; day <= lastDay.getUTCDate(); day++) {
    cells.push({ date: `${y}-${pad2(m)}-${pad2(day)}` })
  }
  while (cells.length % 7 !== 0) cells.push({ date: null })
  return cells
}

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number)
  if (!y || !m) return month
  const next = new Date(Date.UTC(y, m - 1 + delta, 1))
  return `${next.getUTCFullYear()}-${pad2(next.getUTCMonth() + 1)}`
}

function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number)
  if (!y || !m) return month
  return fmtMonthYear(new Date(y, m - 1, 1))
}

/**
 * Build a Map<ISO-date, PersonOut[]> from the leaves array.
 * Includes all leaves visible on the calendar (approved + pending).
 * Deduplicates same person on same date, sorts each day's list by name.
 */
export function buildPeopleOut(
  leaves: ReadonlyArray<CalendarLeave>,
): Map<string, PersonOut[]> {
  const out = new Map<string, PersonOut[]>()

  for (const leave of leaves) {
    // Enumerate every calendar date in [startDate, endDate].
    const start = new Date(leave.startDate)
    const end = new Date(leave.endDate)
    const cursor = new Date(start)
    while (cursor <= end) {
      const iso = cursor.toISOString().slice(0, 10)
      const list = out.get(iso) ?? []
      const alreadyAdded = list.some((p) => p.id === leave.employeeId)
      if (!alreadyAdded) {
        list.push({ id: leave.employeeId, name: leave.employeeName })
        out.set(iso, list)
      }
      cursor.setUTCDate(cursor.getUTCDate() + 1)
    }
  }

  // Sort each day's list alphabetically by name for stable tooltip output.
  for (const list of out.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name))
  }

  return out
}

// ---------------------------------------------------------------------------
// Reducer for the calendar filter bar.
// ---------------------------------------------------------------------------

type CalendarFiltersAction =
  | { kind: 'set-teams'; teams: ReadonlySet<string> }
  | { kind: 'set-types'; types: ReadonlySet<LeaveTypeId> }
  | { kind: 'toggle-blackouts' }
  | { kind: 'toggle-holidays' }
  | { kind: 'toggle-pending' }
  | { kind: 'clear-all' }

const FILTER_DEFAULTS: CalendarFilters = {
  teams: new Set(),
  leaveTypes: new Set(ALL_LEAVE_TYPES),
  showBlackouts: true,
  showHolidays: true,
  showPending: false,
}

// ---------------------------------------------------------------------------
// localStorage persistence — key versioned so schema changes can rev to v2.
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'lms.calendar.viewState.v1'

interface PersistedViewState {
  filters: CalendarFilters
  month: string
}

function readPersistedViewState(): PersistedViewState | null {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return null
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw === null) return null
    const parsed = JSON.parse(raw) as unknown
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !('filters' in parsed) ||
      !('month' in parsed)
    ) {
      return null
    }
    const obj = parsed as {
      filters?: {
        teams?: ReadonlyArray<string>
        leaveTypes?: ReadonlyArray<string>
        showBlackouts?: boolean
        showHolidays?: boolean
        showPending?: boolean
      }
      month?: string
    }
    const f = obj.filters
    const month = obj.month
    if (
      f === undefined ||
      typeof month !== 'string' ||
      !/^\d{4}-\d{2}$/.test(month)
    ) {
      return null
    }
    const filters: CalendarFilters = {
      teams: new Set(Array.isArray(f.teams) ? f.teams : []),
      leaveTypes: new Set(
        (Array.isArray(f.leaveTypes) ? f.leaveTypes : [...ALL_LEAVE_TYPES]).filter(
          (t): t is LeaveTypeId =>
            (ALL_LEAVE_TYPES as ReadonlyArray<string>).includes(t),
        ),
      ),
      // Blackouts + holidays are always shown (their toggles were removed);
      // force true so a stale persisted "off" can't hide them permanently.
      showBlackouts: true,
      showHolidays: true,
      showPending: typeof f.showPending === 'boolean' ? f.showPending : false,
    }
    return { filters, month }
  } catch {
    return null
  }
}

function writePersistedViewState(state: PersistedViewState): void {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return
  }
  try {
    const serialized = {
      filters: {
        teams: [...state.filters.teams],
        leaveTypes: [...state.filters.leaveTypes],
        showBlackouts: state.filters.showBlackouts,
        showHolidays: state.filters.showHolidays,
        showPending: state.filters.showPending,
      },
      month: state.month,
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(serialized))
  } catch {
    // Quota exceeded or storage disabled — silently noop.
  }
}

function calendarFiltersReducer(
  state: CalendarFilters,
  action: CalendarFiltersAction,
): CalendarFilters {
  switch (action.kind) {
    case 'set-teams':
      return { ...state, teams: action.teams }
    case 'set-types':
      return { ...state, leaveTypes: action.types }
    case 'toggle-blackouts':
      return { ...state, showBlackouts: !state.showBlackouts }
    case 'toggle-holidays':
      return { ...state, showHolidays: !state.showHolidays }
    case 'toggle-pending':
      return { ...state, showPending: !state.showPending }
    case 'clear-all':
      return FILTER_DEFAULTS
  }
}

export function CalendarPage(): JSX.Element {
  const { user } = useSession()
  const realId = user?.id ?? null
  const effectiveId = useEffectiveEmployeeId(realId)

  const persisted = useMemo(() => readPersistedViewState(), [])
  const [month, setMonth] = useState<string>(persisted?.month ?? defaultMonth())
  const [todayPulseId, setTodayPulseId] = useState(0)
  const [filters, dispatch] = useReducer(
    calendarFiltersReducer,
    persisted?.filters ?? FILTER_DEFAULTS,
  )
  const [popoverDate, setPopoverDate] = useState<string | null>(null)
  const popoverAnchorRef = useRef<HTMLElement | null>(null)

  // Team selection is explicit and defaults to "all shown". `filters.teams`
  // holds an explicit subset; when it is empty the selection is either ALL
  // (default) or NONE (the user hit "Clear") — `teamsCleared` disambiguates.
  // Not persisted: a reload resets an empty selection back to "all".
  const [teamsCleared, setTeamsCleared] = useState(false)

  useEffect(() => {
    writePersistedViewState({ filters, month })
  }, [filters, month])

  // Employees must not see coverage-critical warnings on any calendar surface.
  // Defined early so it can gate both dayMeta and the day-popover coverage prop.
  const showCoverageWarnings = user !== null && user.role !== 'employee'

  // Manager/HOD see coverage-critical warnings scoped to their own team only —
  // they should not be alerted when another team is under-staffed. HR / Director /
  // Exec Director see coverage across all teams (org-wide responsibility).
  const coverageTeamScope: string | null =
    user !== null && !HR_PLUS_ROLES.has(user.role) && user.teamId
      ? user.teamId
      : null

  // Teams dropdown is available to every role — employees filter the org-level
  // calendar grid by team just like managers/HR do.
  const showTeamFilter = Boolean(user)

  // Always fetch the whole month unscoped, then filter leaves by team on the
  // client. Passing a `team` to the API scoped blackouts/holidays to that team
  // (so the company blackout vanished for other teams) and still leaked the
  // viewer's own cross-team leave — client-side filtering keeps team selection
  // to LEAVES only while blackouts + holidays stay global.
  const calendarQuery = useQuery({
    queryKey: ['calendar', effectiveId, month],
    queryFn: () => getCalendar({ month }),
    enabled: Boolean(user),
  })

  const teamsQuery = useQuery({
    queryKey: ['calendar', 'teams'],
    queryFn: listCalendarTeams,
    enabled: Boolean(user) && showTeamFilter,
  })

  const allTeamIds = useMemo(
    () => new Set((teamsQuery.data ?? []).map((t) => t.id)),
    [teamsQuery.data],
  )

  // Colour each team by its INDEX in the (id-sorted) team list so neighbouring
  // teams get maximally-different hues. Falls back to a hash for any id not in
  // the loaded list (shouldn't happen once teams load).
  const teamColor = useMemo(() => {
    const sorted = [...(teamsQuery.data ?? [])].sort((a, b) =>
      a.id.localeCompare(b.id),
    )
    const indexById = new Map(sorted.map((t, i) => [t.id, i]))
    return (teamId: string): TeamColorStyle => {
      const idx = indexById.get(teamId)
      return idx !== undefined ? teamColorForIndex(idx) : teamColorStyle(teamId)
    }
  }, [teamsQuery.data])

  // Resolve the empty-subset ambiguity: empty + cleared = NONE, else ALL.
  const teamsMode: 'all' | 'none' | 'subset' =
    filters.teams.size > 0 ? 'subset' : teamsCleared ? 'none' : 'all'

  const teamChecked = (id: string): boolean =>
    teamsMode === 'all' ? true : teamsMode === 'none' ? false : filters.teams.has(id)

  function handleSelectAllTeams(): void {
    setTeamsCleared(false)
    dispatch({ kind: 'set-teams', teams: new Set() })
  }

  function handleClearTeams(): void {
    setTeamsCleared(true)
    dispatch({ kind: 'set-teams', teams: new Set() })
  }

  function handleToggleTeam(id: string): void {
    // Materialise the current effective selection, flip `id`, then normalise
    // back to the ALL / NONE sentinels when the result covers everything or
    // nothing so the toolbar summary + checkboxes stay truthful.
    const base =
      teamsMode === 'all'
        ? new Set(allTeamIds)
        : teamsMode === 'none'
          ? new Set<string>()
          : new Set(filters.teams)
    if (base.has(id)) base.delete(id)
    else base.add(id)
    if (allTeamIds.size > 0 && base.size === allTeamIds.size) {
      setTeamsCleared(false)
      dispatch({ kind: 'set-teams', teams: new Set() })
    } else if (base.size === 0) {
      setTeamsCleared(true)
      dispatch({ kind: 'set-teams', teams: new Set() })
    } else {
      setTeamsCleared(false)
      dispatch({ kind: 'set-teams', teams: base })
    }
  }

  // Prune stale team IDs from `filters.teams` once the live team list
  // loads. Persisted filter state can carry IDs from a prior DB snapshot
  // (different env, re-seed, deleted team) that no longer exist —
  // without this pruning the toolbar would render "Teams (13)" while
  // only 6 teams are listed, AND the `filters.teams.has(l.teamId)`
  // filter predicate would silently exclude every leave because none
  // of the stale IDs match.
  useEffect(() => {
    const live = teamsQuery.data
    if (!live || live.length === 0) return
    if (filters.teams.size === 0) return
    const liveIds = new Set(live.map((t) => t.id))
    const pruned = new Set<string>()
    for (const id of filters.teams) {
      if (liveIds.has(id)) pruned.add(id)
    }
    if (pruned.size !== filters.teams.size) {
      dispatch({ kind: 'set-teams', teams: pruned })
    }
    // teamsQuery.data is the only signal we care about; React-Query gives
    // it a stable reference under structural sharing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamsQuery.data])

  const cells = useMemo(() => buildMonthCells(month), [month])

  const navigate = useNavigate()

  const visibleLeaves = useMemo(() => {
    const all = calendarQuery.data?.leaves ?? []
    const viewerId = user?.id
    return all.filter((l) => {
      // Team selection: NONE hides every team; a subset (any size) shows only
      // the checked teams. Applies to the viewer's own leave too, so a leave
      // from an unchecked team never leaks in.
      if (teamsMode === 'none') return false
      if (teamsMode === 'subset' && !filters.teams.has(l.teamId)) return false
      // Type-hidden leaves (privacy sentinel) always pass the type filter —
      // the viewer can't distinguish what they can't see.
      if (l.type !== HIDDEN_LEAVE_TYPE && !filters.leaveTypes.has(l.type))
        return false
      // The viewer's own pending leaves are always visible — the toggle
      // only controls visibility of OTHER people's pending leaves so an
      // employee never gets stuck wondering where the row they just
      // submitted went.
      const isOwn = viewerId !== undefined && l.employeeId === viewerId
      if (l.status === 'pending' && !filters.showPending && !isOwn) {
        return false
      }
      return true
    })
  }, [calendarQuery.data?.leaves, filters, user?.id, teamsMode])

  const teamHeadcount = useMemo(() => {
    const map = new Map<string, number>()
    for (const t of teamsQuery.data ?? []) map.set(t.id, t.headcount)
    return map
  }, [teamsQuery.data])

  const dayMeta = useMemo((): ReadonlyMap<string, MonthGridDayMeta> => {
    const payload = calendarQuery.data
    if (payload === undefined) return new Map<string, MonthGridDayMeta>()
    // Manager/HOD: narrow the leaves used for coverage calculation to their own
    // team so the worst-team coverage scan in coverageForDay can't surface
    // breaches from teams the manager doesn't own. Calendar leaf bars stay
    // unfiltered (visibleLeaves is reused for rendering elsewhere).
    const leavesForCoverage =
      coverageTeamScope !== null
        ? visibleLeaves.filter((l) => l.teamId === coverageTeamScope)
        : visibleLeaves
    const raw = buildDayMetaWithSignals(
      { ...payload, leaves: leavesForCoverage },
      filters,
      teamHeadcount,
    ) as ReadonlyMap<string, MonthGridDayMeta>
    if (showCoverageWarnings) return raw
    // Employees: strip coverage ribbons from MonthGrid cells.
    const stripped = new Map<string, MonthGridDayMeta>()
    for (const [date, meta] of raw) {
      const { coverageWarning: _dropped, ...rest } = meta
      stripped.set(date, rest)
    }
    return stripped
  }, [calendarQuery.data, visibleLeaves, filters, teamHeadcount, showCoverageWarnings, coverageTeamScope])

  const visibleBlackouts = filters.showBlackouts
    ? (calendarQuery.data?.blackouts ?? [])
    : []

  const visibleHolidays = filters.showHolidays
    ? (calendarQuery.data?.publicHolidays ?? [])
    : []

  const peopleOutFiltered = useMemo(
    () => buildPeopleOut(visibleLeaves),
    [visibleLeaves],
  )

  const presentLeaveTypes = useMemo(
    () =>
      Array.from(
        new Set(
          visibleLeaves
            .map((l) => l.type)
            .filter((t): t is LeaveTypeId => t !== HIDDEN_LEAVE_TYPE),
        ),
      ),
    [visibleLeaves],
  )

  const hasConflicts = useMemo(
    () =>
      visibleLeaves.some((l) =>
        leaveConflictsBlackout(l, calendarQuery.data?.blackouts ?? []),
      ),
    [visibleLeaves, calendarQuery.data?.blackouts],
  )

  const teamCounts = useMemo(
    () =>
      (teamsQuery.data ?? [])
        .map((t) => ({
          id: t.id,
          name: t.name,
          count: visibleLeaves.filter((l) => l.teamId === t.id).length,
        }))
        .filter((t) => t.count > 0),
    [teamsQuery.data, visibleLeaves],
  )

  function handleFiltersChange(next: CalendarFilters): void {
    // A leaveTypes set with size 0 means "clear all" was fired from the toolbar
    // (toolbar's DEFAULT_FILTERS has leaveTypes: new Set()). We treat that as
    // "reset to page defaults" which includes all types checked.
    const isDefault =
      next.teams.size === 0 &&
      (next.leaveTypes.size === 0 || next.leaveTypes.size === ALL_LEAVE_TYPES.length) &&
      next.showBlackouts === FILTER_DEFAULTS.showBlackouts &&
      next.showHolidays === FILTER_DEFAULTS.showHolidays &&
      next.showPending === FILTER_DEFAULTS.showPending
    if (isDefault) {
      // "Clear filters" resets to the default, which shows every team.
      setTeamsCleared(false)
      dispatch({ kind: 'clear-all' })
      return
    }
    if (next.teams !== filters.teams) dispatch({ kind: 'set-teams', teams: next.teams })
    if (next.leaveTypes !== filters.leaveTypes)
      dispatch({ kind: 'set-types', types: next.leaveTypes })
    if (next.showBlackouts !== filters.showBlackouts) dispatch({ kind: 'toggle-blackouts' })
    if (next.showHolidays !== filters.showHolidays) dispatch({ kind: 'toggle-holidays' })
    if (next.showPending !== filters.showPending) dispatch({ kind: 'toggle-pending' })
  }

  function handleDayClick(date: string, anchor: HTMLElement): void {
    const hasLeaves = visibleLeaves.some(
      (l) => l.startDate <= date && l.endDate >= date,
    )
    const hasHoliday = visibleHolidays.some((h) => h.date === date)
    const hasBlackout = visibleBlackouts.some(
      (b) => b.startDate <= date && b.endDate >= date,
    )
    if (!hasLeaves && !hasHoliday && !hasBlackout) return
    popoverAnchorRef.current = anchor
    setPopoverDate(date)
  }

  function handleLeaveClick(leaveId: string): void {
    setPopoverDate(null)
    void navigate({ to: '/leave', search: { id: leaveId } })
  }

  const isHrPlus = user !== null && HR_PLUS_ROLES.has(user.role)
  const isManagerOrHod = user !== null && (user.isManager || user.isHod)

  // HR/Director/ExecDirector can navigate to any leave; manager/HOD only their team's.
  // Plain employees see non-interactive bars.
  const popoverLeaveClickHandler: ((leaveId: string) => void) | undefined =
    isHrPlus || isManagerOrHod ? handleLeaveClick : undefined

  const popoverCanClickLeave: ((leave: CalendarLeave) => boolean) | undefined =
    isManagerOrHod && !isHrPlus
      ? (l) => l.teamId === user!.teamId
      : undefined

  return (
    <div className="page" data-screen-label="Calendar">
      {/* ── Page header — matches sibling pages (.between + .h1) ── */}
      <div className="between" style={{ marginBottom: 22 }}>
        <h1 className="h1">Calendar</h1>
      </div>

      {/* ── Toolbar ── */}
      <CalendarToolbar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        teamOptions={teamsQuery.data ?? []}
        monthLabel={monthLabel(month)}
        onPrevMonth={() => setMonth((m) => shiftMonth(m, -1))}
        onNextMonth={() => setMonth((m) => shiftMonth(m, 1))}
        onToday={() => {
          setMonth(defaultMonth())
          setTodayPulseId((n) => n + 1)
        }}
        canSeePending={isHrPlus || isManagerOrHod}
        teamChecked={teamChecked}
        onToggleTeam={handleToggleTeam}
        onSelectAllTeams={handleSelectAllTeams}
        onClearTeams={handleClearTeams}
        teamColor={teamColor}
      />

      {/* ── Month grid ── */}
      <div data-testid="calendar-grid">
        <TeamCalendarGrid
          cells={cells}
          leaves={visibleLeaves}
          blackouts={visibleBlackouts}
          publicHolidays={visibleHolidays}
          today={todayISO()}
          ownTeamId={
            !effectiveId || effectiveId !== realId ? null : user?.teamId ?? null
          }
          peopleOut={peopleOutFiltered}
          dayMeta={dayMeta}
          onCellClick={handleDayClick}
          todayPulseId={todayPulseId}
          weekStartsOn="sunday"
          teamColor={teamColor}
        />
      </div>

      {/* ── Footer legend ── */}
      <CalendarLegend presentLeaveTypes={presentLeaveTypes} />

      {popoverDate !== null ? (
        <CalendarDayPopover
          date={popoverDate}
          teamColor={teamColor}
          leaves={visibleLeaves.filter(
            (l) => l.startDate <= popoverDate && l.endDate >= popoverDate,
          )}
          {...(visibleHolidays.find((h) => h.date === popoverDate) !== undefined
            ? {
                publicHoliday: {
                  name: visibleHolidays.find((h) => h.date === popoverDate)!.name,
                },
              }
            : {})}
          blackouts={visibleBlackouts.filter(
            (b) => b.startDate <= popoverDate && b.endDate >= popoverDate,
          )}
          {...(() => {
            // Coverage warnings are suppressed for employees — they see the
            // popover for leave details only, not staffing-level metrics.
            if (!showCoverageWarnings) return {}
            const cov = dayMeta.get(popoverDate)?.coverageWarning
            if (cov === undefined) return {}
            const teamAbsent = new Map<string, Set<string>>()
            for (const l of visibleLeaves) {
              if (l.startDate > popoverDate || l.endDate < popoverDate) continue
              if (filters.teams.size > 0 && !filters.teams.has(l.teamId)) continue
              // Manager/HOD: only their own team's absent count is relevant —
              // coverage-critical is scoped to their team in dayMeta, so the
              // breakdown shouldn't leak other teams' staffing either.
              if (coverageTeamScope !== null && l.teamId !== coverageTeamScope) continue
              const set = teamAbsent.get(l.teamId) ?? new Set<string>()
              set.add(l.employeeId)
              teamAbsent.set(l.teamId, set)
            }
            const teamMeta = new Map<string, { name: string; headcount: number }>()
            for (const t of teamsQuery.data ?? []) {
              teamMeta.set(t.id, { name: t.name, headcount: t.headcount })
            }
            const teams = [...teamAbsent.entries()]
              .map(([teamId, employees]) => {
                const meta = teamMeta.get(teamId)
                const headcount = meta?.headcount ?? 0
                const ratio = headcount > 0 ? employees.size / headcount : 0
                return {
                  teamName: meta?.name ?? teamId,
                  absentCount: employees.size,
                  headcount,
                  ratio,
                }
              })
              .filter((t) => t.headcount > 0)
              .sort((a, b) => b.ratio - a.ratio)
            return { coverage: { level: cov, teams } }
          })()}
          open={true}
          onClose={() => setPopoverDate(null)}
          anchorRef={popoverAnchorRef as RefObject<HTMLElement>}
          {...(popoverLeaveClickHandler !== undefined
            ? { onLeaveClick: popoverLeaveClickHandler }
            : {})}
          {...(popoverCanClickLeave !== undefined
            ? { canClickLeave: popoverCanClickLeave }
            : {})}
        />
      ) : null}
    </div>
  )
}
